import type { BlogPostProps } from '@/types/blog';

export type BlogPostDetail = BlogPostProps & {
  content: string;
  relatedSlugs?: string[];
  faqs?: { question: string; answer: string }[];
};

export const blogPosts: BlogPostDetail[] = [
  {
    id: '1',
    title: "Les nouvelles obligations en matière de document unique d'évaluation des risques",
    excerpt:
      "Depuis le 1er avril 2025, de nouvelles obligations concernent le document unique d'évaluation des risques professionnels (DUERP). Découvrez les changements à mettre en place dans votre entreprise.",
    date: '2025-05-05',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Réglementation',
    slug: 'nouvelles-obligations-document-unique',
    imageSrc: '/images/blog/duerp.webp',
    content: `
      <h2>Les évolutions réglementaires du Document Unique</h2>
      <p>La loi "Santé au travail" du 2 août 2021 a renforcé les obligations des employeurs en matière d'évaluation et de prévention des risques professionnels. Depuis le 1er avril 2025, de nouvelles dispositions sont entrées en vigueur et modifient la gestion du DUERP.</p>
      <h3>Conservation et traçabilité</h3>
      <p>Les entreprises doivent conserver toutes les versions du document pendant 40 ans. Cette mesure vise à assurer une traçabilité des expositions professionnelles tout au long de la carrière des salariés.</p>
      <h3>Dématérialisation obligatoire</h3>
      <p>Le DUERP doit être dématérialisé et déposé sur un portail numérique sécurisé. Les salariés et anciens salariés doivent pouvoir accéder aux versions couvrant leurs périodes d'activité.</p>
      <h3>Renforcement du contenu</h3>
      <ul>
        <li>Liste détaillée des risques professionnels identifiés</li>
        <li>Mesures de prévention existantes</li>
        <li>Plan d'action avec indicateurs de suivi</li>
        <li>Attention renforcée aux risques chimiques et facteurs de pénibilité</li>
      </ul>
      <h2>Comment se mettre en conformité ?</h2>
      <ol>
        <li>Vérifier la conformité du DUERP actuel</li>
        <li>Planifier la mise à jour annuelle minimale</li>
        <li>Associer les salariés à la démarche</li>
        <li>Dématérialiser et historiser chaque version</li>
      </ol>
      <p>Nos formations vous accompagnent dans la mise en conformité réglementaire et l'amélioration durable de la prévention.</p>
    `,
    relatedSlugs: ['risques-psychosociaux-identifier-prevenir', 'organiser-exercice-evacuation-incendie'],
    faqs: [
      {
        question: 'À quelle fréquence faut-il mettre à jour le DUERP ?',
        answer: 'Au minimum une fois par an, et à chaque changement important impactant les conditions de travail.',
      },
      {
        question: 'Le DUERP est-il obligatoire pour les petites entreprises ?',
        answer: 'Oui, quelle que soit la taille de l’entreprise.',
      },
    ],
  },
  {
    id: '2',
    title: 'Prévenir les troubles musculosquelettiques en télétravail',
    excerpt:
      "Le télétravail peut entraîner des risques pour la santé si le poste de travail n'est pas adapté. Voici des actions concrètes pour prévenir les TMS.",
    date: '2025-04-28',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Ergonomie',
    slug: 'prevenir-tms-teletravail',
    imageSrc: '/images/blog/teletravail.webp',
    content: `
      <h2>Identifier les facteurs de risque</h2>
      <p>Les postures statiques prolongées, l’absence de pauses actives et un mobilier inadapté augmentent fortement le risque de TMS.</p>
      <h3>Les bonnes pratiques</h3>
      <ul>
        <li>Régler la hauteur de l’écran à hauteur des yeux</li>
        <li>Conserver les coudes proches du corps</li>
        <li>Alterner positions assise et debout</li>
        <li>Faire une pause active toutes les 60 à 90 minutes</li>
      </ul>
      <p>La prévention des TMS en télétravail repose sur l’ergonomie, la sensibilisation et un suivi managérial adapté.</p>
    `,
    relatedSlugs: ['risques-psychosociaux-identifier-prevenir'],
  },
  {
    id: '3',
    title: "Comment organiser efficacement un exercice d'évacuation incendie",
    excerpt:
      "L'exercice d'évacuation incendie est une obligation légale et un levier clé pour la sécurité. Découvrez une méthode opérationnelle.",
    date: '2025-04-15',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Incendie',
    slug: 'organiser-exercice-evacuation-incendie',
    imageSrc: '/images/blog/incendie.webp',
    content: `
      <h2>Préparer l’exercice</h2>
      <p>Définissez les objectifs (temps d’évacuation, coordination, points de blocage), informez les rôles clés et vérifiez le matériel.</p>
      <h3>Conduire et débriefer</h3>
      <p>Réalisez l’exercice dans des conditions réalistes puis formalisez un retour d’expérience avec plan d’action correctif.</p>
    `,
  },
  {
    id: '4',
    title: 'Les gestes qui sauvent : que faire face à un arrêt cardiaque ?',
    excerpt:
      "Connaître les gestes de premiers secours peut sauver des vies. Focus sur la conduite à tenir face à un arrêt cardiaque.",
    date: '2025-04-02',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Secourisme',
    slug: 'gestes-qui-sauvent-arret-cardiaque',
    imageSrc: '/images/blog/secourisme.webp',
    content: `
      <h2>Les 3 réflexes vitaux</h2>
      <ol>
        <li>Alerter immédiatement les secours</li>
        <li>Commencer le massage cardiaque</li>
        <li>Utiliser un défibrillateur dès disponibilité</li>
      </ol>
      <p>Une intervention précoce augmente significativement les chances de survie.</p>
    `,
  },
  {
    id: '5',
    title: 'Les risques psychosociaux au travail : comment les identifier et les prévenir',
    excerpt:
      'Stress, harcèlement, burnout : comment structurer une prévention efficace des RPS dans votre organisation ?',
    date: '2025-03-20',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Prévention',
    slug: 'risques-psychosociaux-identifier-prevenir',
    imageSrc: '/images/blog/rps.webp',
    content: `
      <h2>Les signaux faibles à surveiller</h2>
      <p>Absentéisme, tensions interpersonnelles, surcharge chronique et perte d’engagement sont des indicateurs à traiter rapidement.</p>
      <h3>Structurer la prévention</h3>
      <ul>
        <li>Évaluer les facteurs organisationnels</li>
        <li>Former l’encadrement de proximité</li>
        <li>Mettre en place des espaces d’expression</li>
      </ul>
    `,
  },
  {
    id: '6',
    title: 'Habilitation électrique : niveaux et personnes concernées',
    excerpt:
      "L'habilitation électrique est obligatoire pour les opérations sur ou à proximité d'installations électriques. Voici les repères essentiels.",
    date: '2025-03-05',
    author: {
      name: 'Équipe SDPSI Formation',
      image: '/images/home/sdpsilogo.png',
    },
    category: 'Habilitations',
    slug: 'habilitation-electrique-niveaux-personnes-concernees',
    imageSrc: '/images/blog/electrique.webp',
    content: `
      <h2>Comprendre les niveaux d’habilitation</h2>
      <p>Le niveau dépend des opérations réalisées et de l’environnement de travail (B0, H0, BS, BE, etc.).</p>
      <p>La formation et l’évaluation doivent être adaptées au poste et actualisées régulièrement.</p>
    `,
  },
];

export const blogCategories = ['Toutes', ...new Set(blogPosts.map((post) => post.category))];

export function getBlogPostBySlug(slug: string) {
  return blogPosts.find((post) => post.slug === slug);
}
